import _ from 'lodash';
import CacheableDataObject from './CacheableDataObject';
import Category from './Category';
import moment from 'moment';
import moneyFormat, { moneyFormatFromCents } from '../utilities/moneyFormat';
import budgetCalculations from "utilities/budgetCalculations/budgetCalculations";
import DateHelper from "utilities/DateHelper";

export default class Budget extends CacheableDataObject {

	beforeConstruct() {
		this.instanceNumber = --Budget.instanceCount;
		this.original = null;
		this.amount = 0;

		this.notes = '';
		this.category_id = null;

		this.month = null;
		this.year = null;
		this.date = null;

		this.interest_amount = '';

		this.fund_total = '';
		this.minimum_payment = '';
		this.rollover_state = null;
	}

	hasActivity() {
		if(this.amount !== 0) {
			return true;
		}
		if(this.getTransactions().length > 0) {
			return true;
		}
		return false;
	}

	hasChanges() {
		if(!this.original) {
			throw new Error('Using an original object to see if it has changes');
		}
		const fieldsToCheck = ['amount', 'fund_total', 'minimum_payment', 'interest_amount', 'notes', 'rollover_state'];
		const fieldsWithChanges = [];
		for(let field of fieldsToCheck) {
			if(this.original[field] !== this[field]) {
				fieldsWithChanges.push(field);
			}
		}

		if(fieldsWithChanges.length > 0) {
			console.log('has changes', this.category.name);
		}
		for(let fieldsWithChange of fieldsWithChanges) {
			console.log('none matching fields', fieldsWithChange, '-original-', this.original[fieldsWithChange], '-me-', this[fieldsWithChange]);
		}
		return fieldsWithChanges.length > 0;
	}

	afterUpdate() {
		if(this.month !== null && this.year !== null) {
			this.date = moment().year(this.year).month(this.month);
		}
	}


	clone() {
		const budget = Budget.create().copy(this);
		budget.original = this;
		budget.beforeChanges = Budget.create().copy(this);
		return budget;
	}

	revert() {
		this.copy(this.beforeChanges);
	}

	copy(original) {
		this.fund_total = original.fund_total;
		this.amount = original.amount;
		this.minimum_payment = original.minimum_payment;
		this.interest_amount = original.interest_amount;
		this.notes = original.notes;
		this.rollover_state = original.rollover_state;
		this.name = original.name;
		this.category = original.category;
		this.category_id = original.category_id;
		this.date = original.date;
		this.year = original.year;
		this.month = original.month;
		this.show_when_not_used = original.show_when_not_used;
		return this;
	}

	getTransactions() {
		return this.category.getTransactionsForBudget(this);
	}

	getTransactionsForBudgetIncludingChildren(category){
		const transactions = category.getTransactionsForBudget(this);
		_.each(category.children, (childCategory) => {
			const childTransactions = childCategory.getTransactionsForBudget(this);
			for(let childTransaction of childTransactions) {
				transactions.push(childTransaction);
			}
		});
		return transactions;
	}

	/**
	 * Total amount of money allocated to the budget
	 * @returns {number|*}
	 */
	getTotalAvailable() {
		return this.amount;
	}

	getBudgeted() {
		return this.amount ? Number(this.amount) : 0;
	}

	getSpent() {
		const transactions = this.getTransactions();
		return budgetCalculations.getTotalSpentFromTransactions(transactions, this.category);
	}

	getRemaining() {
		const transactions = this.getTransactions();
		let total = Number(this.getTotalAvailable());

		const totalSpent = budgetCalculations.getTotalSpentFromTransactions(transactions, this.category);
		total -= totalSpent;
		return total;
	}

	hasLoanData() {
		return this.fund_total || this.minimum_payment || this.interest_amount;
	}

	isRolloverStart() {
		return this.rollover_state === 'START';
	}

	isRolloverEnd() {
		return this.rollover_state === 'END';
	}

	getSummary(categoryGroup) {
		if(this.name) {
			return this.name;
		}
		switch(categoryGroup.type) {
			case Category.types.EXPENSE:
			case Category.types.INCOME:
			case Category.types.SAVINGS:
				return `${this.date.format('MMMM')} ${this.date.format('YYYY')} - ${moneyFormatFromCents(this.amount)}`;
			case Category.types.LOANS:
				return `${this.date.format('MMMM')} ${this.date.format('YYYY')} - ${moneyFormatFromCents(this.amount)} Balance: ${moneyFormatFromCents(this.fund_total)} Interest: ${this.interest_amount}`;
			default:
				return '????';
		}
	}

	/**
	 * Returns true if the other budget is the same category and date as this budget
	 * mostly exists so that MultiBudget can say whether or not it contains a budget
	 * @param budget
	 * @returns {boolean}
	 */
	contains(budget) {
		if(budget.category_id !== this.category_id) {
			return false;
		}
		return DateHelper.yearMonthSerialize(this.date) === DateHelper.yearMonthSerialize(budget.date);
	}
}

const setDefaultFieldValue = (budget, data, field) => {
	if(data[field] === null) {
		budget[field] = '';
	} else {
		budget[field] = data[field];
	}
};
Budget.instanceCount = -1;
Budget.prototype.dataLocationParsers = {
	amount: setDefaultFieldValue,
	fund_total: setDefaultFieldValue,
	minimum_payment: setDefaultFieldValue,
	notes: (budget, data) => {
		budget.notes = data.notes;
	},
	interest_amount: setDefaultFieldValue,
	month: (budget, data) => {
		budget.month = data.month - 1;
	},
	category: (budget, data) => {
		budget.category = data.category;
		budget.category_id = data.category.id;
	},
	rollover_state: (budget, data) => {
		budget.rollover_state = data.rollover_state;
	},
	rolloverStartField: (budget, data) => {
		if(data.rolloverStartField === true) {
			budget.rollover_state = 'START';
		} else {
			budget.rollover_state = null;
		}
	},
	rolloverEndField: (budget, data) => {
		if(data.rolloverEndField === true) {
			budget.rollover_state = 'END';
		} else {
			budget.rollover_state = null;
		}
	}
};

